<template lang="pug">
.project__liked(
  class="relative translate-y-[10px]",
  :class="ended ? 'pointer-events-none opacity-80' : ''",
  @click="handleLike",
  role="button",
  :aria-disabled="ended ? 'true' : 'false'",
)
  img.w-auto.h-auto(
    :src="iconSrc",
    alt="いいね",
    class="cursor-pointer"
  )
  .project__likedNumWrap(
    class="flex justify-center items-center w-[40px] absolute top-[12px] right-[6px] cursor-pointer"
  )
    .project__likedNum.txt__osM24(
      :class="isWhite ? 'text-white' : 'text-[#7E7E7E]'"
    ) {{ displayCount }}

  //- 取り消しリンク（ログインユーザーのみ）
  //- div(
  //-   v-if="canCancel",
  //-   class="mt-1 text-right"
  //- )
  //- button(
  //-   type="button",
  //-   class="underline text-sm",
  //-   :class="canCancel ? 'text-gray-700 hover:text-red-500' : 'text-gray-400 cursor-not-allowed'",
  //-   :disabled="!canCancel",
  //-   @click="handleReset"
  //- ) 取り消す

</template>

<script setup lang="ts">
import { ref, onMounted, computed, toRefs } from 'vue';
import axios from 'axios';
// const props = defineProps<{ projectId: number }>();
const props = withDefaults(defineProps<{
  projectId: number;
  activeSrc?: string;
  inactiveSrc?: string;
  endedSrc?: string;
  ended?: boolean;
}>(), {
  // デフォルトのアイコンパス（必要に応じて実際のパスに合わせてください）
  // activeSrc: '/modules/images/likes_red.svg',
  // inactiveSrc: '/modules/images/likes_gray.svg',
});

const { activeSrc, inactiveSrc, endedSrc, ended: endedProp } = toRefs(props);
// const project = (window as any).project;

// const baseCount = ref<number>(0)
// このユーザーのローカル加算（押した回数）
// const userCount = ref<number>(0)
// サーバから取得する合計カウント(誰でも表示)
const totalCount = ref<number>(0)
//- ログイン中ユーザーがこのプロジェクトに付けた「自分のいいね数」
const myLikes = ref<number>(0)
// 取り消し可能か（= ログイン済み && 自分のいいねが1件以上）
const canCancel = ref<boolean>(false);
// 未ログイン時に押した直後だけ赤く見せるためのローカルフラグ（リロードで消える）
const localActive = ref(false)

const isPopping = ref(false);
// 開催終了フラグ（APIの応答で取得する想定）
const ended = ref(false);
// いいね数1以上でアクティブ（視覚上の基準）
const isActive = computed(() => totalCount.value > 0);
// 表示色（白表示になる条件）: 開催終了またはいいねが1以上
const isWhite = computed(() => ended.value || totalCount.value > 0);
// アイコン選択（終了が優先）
const iconSrc = computed(() => {
  if (ended.value) return endedSrc?.value ?? inactiveSrc.value;
  return totalCount.value > 0 ? (activeSrc.value ?? inactiveSrc.value) : (inactiveSrc.value ?? activeSrc.value);
});

const displayCount = computed(() => Math.min(totalCount.value, 999));


// props から受け取る ended が文字列 'true'/'false' の場合に正しく解釈するヘルパ
function parseBoolProp(v: any): boolean {
  if (v === true) return true;
  if (v === false) return false;
  if (typeof v === 'string') {
    const lower = v.toLowerCase().trim();
    return lower === 'true' || lower === '1';
  }
  if (typeof v === 'number') return v === 1;
  return false;
}

onMounted(async() => {
  try {
    // console.log(props.projectId);
    if (!props.projectId) return;

    // 親コンポーネントから `ended` プロップが渡されている場合は優先して設定
    if (typeof endedProp?.value !== 'undefined') {
      ended.value = parseBoolProp(endedProp.value);
    }

    const { data, status } = await axios.get(`/like/${props.projectId}`);
    if (status !== 200) throw new Error('Failed to fetch likes');

    // サーバ側のキー名の違いに柔軟対応 ??
    totalCount.value = Number(
      data.total_likes ?? data.likes_count ?? data.like_count ?? 0
    );
    // API が終了情報を返す場合に対応（ended=true または event_end_at）
    // ただし親プロップが与えられている場合はプロップを優先する
    if (typeof endedProp?.value === 'undefined') {
      if (typeof data.ended !== 'undefined') {
        ended.value = Boolean(data.ended);
      } else if (data.event_end_at) {
        try {
          ended.value = new Date(data.event_end_at) < new Date();
        } catch (e) {
          ended.value = false;
        }
      }
    }
    // 終了なら取り消し不可にする
    if (ended.value) {
      canCancel.value = false;
    }
    myLikes.value = Number(data.my_likes ?? 0);
    canCancel.value = Boolean(data.can_cancel ?? (myLikes.value > 0));
    localActive.value = false;
    // console.log(data);
  } catch (err) {
    console.error('いいね数の取得に失敗', err)
  }
  // console.log('VueLikedButton mounted');
});

async function handleLike() {
  // console.log('handleLike');
  // 終了イベントは送信しない
  if (ended.value) {
    // console.log('handleLike prevented: project ended');
    return;
  }

  try {
    const { data, status } = await axios.post(`/like/${props.projectId}`);
    if (status !== 200) return;
    // POSTの返り値にもキー差異があり得るのでフォールバックを用意
    const newTotal = Number(
      data.total_likes ?? data.likes_count ?? data.like_count ?? totalCount.value + 1
    );
    const newMine = Number(
      data.my_likes ?? myLikes.value + 1 // ログイン中で my_likes が返らない場合に備えて+1
    );

    totalCount.value = newTotal;
    myLikes.value = newMine;
    canCancel.value = newMine > 0;

    // ログインしていない場合（= canCancel が false かつ my_likes が 0）のときは
    // 見た目だけ赤くするためにローカルフラグを立てる（リロードで消える）
    if (!canCancel.value && myLikes.value === 0) {
      localActive.value = true
    } else {
      // ログインユーザーで my_likes > 0 になったらローカルは不要
      localActive.value = false
    }

    // 軽いポップアニメーション
    // isPopping.value = false;
    // requestAnimationFrame(() => {
    //   isPopping.value = true;
    //   setTimeout(() => (isPopping.value = false), 220);
    // });
  } catch (e) {
    console.error('いいねの送信に失敗', e);
  }
}

async function handleReset() {
  // console.log('handleReset');
  // 終了イベントは取り消しも不可
  if (ended.value) return;
  if (!canCancel.value) return;
  // console.log('handleReset');
  try {
    const { data, status } = await axios.delete(`/like/${props.projectId}`);
    if (status !== 200) return;
    totalCount.value = Number(
      data.total_likes ?? data.likes_count ?? data.like_count ?? Math.max(0, totalCount.value - 1)
    );
    // サーバが返す場合を優先。無ければ 1 減らす
    myLikes.value = Number(
      data.my_likes ?? Math.max(0, myLikes.value - 1)
    );
    canCancel.value = myLikes.value > 0;
  } catch (e: any) {
    if (e?.response?.status === 401) {
      // 未ログインは取り消し不可
      canCancel.value = false;
    }
    console.error('いいね取り消しに失敗', e);
  }
}

</script>

<style lang="sass" scoped>

</style>
